import { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2, PackageOpen, AlertTriangle } from "lucide-react";

// Feature 33 — Evidence Takeout (free). Streams directly through the browser's download manager via
// a same-origin, HttpOnly-session-cookie-authenticated GET — the ZIP is NEVER buffered into a JS
// Blob, so large property/account archives don't exhaust device memory. A small authenticated
// preflight (api.get, cookie-bound) confirms access before the download starts. No bearer token or
// credential ever goes in the URL, and no long-lived/public URL is created. Honest copy: we say the
// download STARTED (we cannot observe browser-side completion), never that it completed.
export function EvidenceTakeout({ scope = "property", propertyId }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const isAccount = scope === "account";

  const run = async () => {
    setBusy(true);
    setFailed(false);
    try {
      // Authenticated preflight (cookie + bearer via axios). Confirms the session and, for a
      // property, ownership/existence (protected 404) before we hand off to the download manager.
      const preflight = isAccount ? "/me" : `/properties/${propertyId}`;
      await api.get(preflight);

      // Trigger a normal same-origin download. The HttpOnly session cookie authenticates it and the
      // response (Content-Disposition: attachment) streams straight to disk — no in-memory Blob.
      const url = isAccount ? `${API}/account/takeout` : `${API}/properties/${propertyId}/takeout`;
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.setAttribute("download", "");
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success("Download started — check your browser's downloads for progress and completion.");
    } catch (e) {
      setFailed(true);
      const msg = e?.response?.status === 404
        ? "We couldn't find that property for your account."
        : "Couldn't start the export. Check your connection and try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const testId = isAccount ? "account-takeout" : "property-takeout";
  return (
    <div data-testid={`${testId}-card`} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-xl bg-[#EEF2FF] p-2.5 text-[#4338CA]"><PackageOpen size={20} /></div>
        <div className="min-w-0">
          <h3 className="font-heading text-base font-semibold text-navy">
            {isAccount ? "Download all your evidence" : "Download this property's evidence"}
          </h3>
          <p className="text-sm text-[#475569] mt-1">
            {isAccount
              ? "Get one ZIP with every property you own — original photos, videos and documents, plus a plain-language record of what TenantSeal has stored."
              : "Get a ZIP of the original photos, videos and documents for this property, with a plain-language record of what TenantSeal has stored."}
          </p>
        </div>
      </div>

      <Button
        data-testid={`${testId}-btn`}
        onClick={run}
        disabled={busy}
        className="mt-4 w-full sm:w-auto rounded-xl bg-navy hover:bg-navy/90 text-white"
      >
        {busy
          ? <><Loader2 size={16} className="mr-2 animate-spin" /> Starting download…</>
          : <><Download size={16} className="mr-2" /> {isAccount ? "Download account archive (.zip)" : "Download evidence archive (.zip)"}</>}
      </Button>

      {failed && (
        <p data-testid={`${testId}-error`} className="mt-3 text-sm text-[#B91C1C] flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          The export didn't start. Check your connection and try again.
        </p>
      )}

      <p className="mt-3 text-xs text-[#94A3B8] leading-relaxed">
        Free. The archive streams straight to your device's downloads — large exports may take a
        while, so watch your browser's download list for progress and completion. Files are copied
        exactly as stored. This archive is not certified, court-ready, or a legally complete record;
        the README explains how to check each file's SHA-256 fingerprint.
      </p>
    </div>
  );
}
